import { cn } from "@/lib/utils";

/** Shared tiny form primitives for the workflow config rail. Kept in one
 * file to avoid a per-widget explosion. */

export function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  readOnly,
  mono,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  mono?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      className={cn(
        "w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-text-primary outline-none transition-colors focus:border-accent",
        readOnly && "cursor-not-allowed opacity-60",
        mono && "font-mono text-xs",
      )}
    />
  );
}

export function Textarea({
  value,
  onChange,
  placeholder,
  readOnly,
  rows = 4,
  resize = true,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  rows?: number;
  /** Allow the native drag-to-resize handle. Off for fields that expand into
   *  a popup instead (e.g. the agent prompt template). */
  resize?: boolean;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      rows={rows}
      className={cn(
        "block w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-accent",
        resize ? "resize-y" : "resize-none",
        readOnly && "cursor-not-allowed opacity-60",
      )}
    />
  );
}

export function Select({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={cn(
        "w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-text-primary outline-none transition-colors focus:border-accent",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Labelled tickbox with a line of explanation under it. Shared because the
 * explanation is usually the point: these toggles change runtime behaviour the
 * author cannot otherwise see.
 */
export function WorkflowToggle({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={`flex items-start gap-2.5 rounded-md border border-border bg-background px-3 py-2 ${disabled ? "opacity-60" : "cursor-pointer"}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
      />
      <span className="min-w-0">
        <span className="block text-sm text-text-primary">{label}</span>
        <span className="mt-0.5 block text-[11px] leading-relaxed text-text-muted">
          {description}
        </span>
      </span>
    </label>
  );
}
